import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Loader2, Upload, Trash2, ArrowUp, ArrowDown, Image as ImageIcon, CheckCircle2, AlertTriangle, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { mediaUrl } from "@/lib/api";
import { getMedia, uploadMedia, deleteMedia, reorderMedia, updateMedia } from "@/lib/adminApi";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/svg+xml";

function DropZone({ kind, multiple, label, hint, onDone, testId }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) {
      toast.error("Lütfen görsel dosyası seçin (jpg, png, webp)");
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      const res = await uploadMedia(kind, multiple ? files : files.slice(0, 1), setProgress);
      const ok = (res.items || []).length;
      if (ok) toast.success(`${ok} görsel yüklendi`);
      (res.errors || []).forEach((e) => toast.error(`${e.file}: ${e.error}`));
      onDone && onDone();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Yükleme başarısız");
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      data-testid={testId}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
      className={`rounded-2xl border-2 border-dashed p-6 text-center transition-colors duration-200 ${
        drag ? "border-[#C8A24A] bg-[#C8A24A]/10" : "border-white/15 bg-[#141517]"
      }`}
    >
      <input ref={inputRef} type="file" accept={ACCEPT} multiple={multiple} className="hidden"
        data-testid={`${testId}-input`} onChange={(e) => handleFiles(e.target.files)} />
      <Upload className="w-7 h-7 text-[#C8A24A] mx-auto" />
      <div className="text-[#EDE7DA] mt-3 font-medium">{label}</div>
      <div className="text-xs text-[#9A9387] mt-1">{hint}</div>
      <Button type="button" disabled={busy} onClick={() => inputRef.current?.click()}
        className="mt-4 bg-[#C8A24A] text-[#141517] hover:bg-[#D7B86A] font-semibold" data-testid={`${testId}-button`}>
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
        {busy ? "Yükleniyor..." : "Dosya Seç"}
      </Button>
      {busy && <Progress value={progress} className="mt-4 h-1.5 bg-white/10" />}
    </div>
  );
}

export default function MediaPanel() {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["admin-media"], queryFn: () => getMedia() });
  const items = (data && data.items) || [];
  const gallery = items.filter((i) => i.kind === "gallery");
  const logo = items.find((i) => i.kind === "logo");
  const hero = items.find((i) => i.kind === "hero");
  const storageOk = data ? data.storage_configured : true;
  const [altDraft, setAltDraft] = useState({});

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-media"] });
    qc.invalidateQueries({ queryKey: ["settings"] });
  };

  const remove = async (id, label) => {
    if (!window.confirm(`${label} silinsin mi?`)) return;
    try {
      await deleteMedia(id);
      toast.success("Silindi");
      refresh();
    } catch {
      toast.error("Silinemedi");
    }
  };

  const move = async (index, dir) => {
    const ids = gallery.map((g) => g.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    try {
      await reorderMedia(ids);
      refresh();
    } catch {
      toast.error("Sıralama kaydedilemedi");
    }
  };

  const saveAlt = async (id) => {
    const alt = altDraft[id];
    if (alt === undefined) return;
    try {
      await updateMedia(id, { alt });
      toast.success("Açıklama kaydedildi");
      refresh();
    } catch {
      toast.error("Kaydedilemedi");
    }
  };

  return (
    <div className="space-y-10" data-testid="media-panel">
      {!storageOk && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="storage-warning">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          Depolama yapılandırılmadı. `.env` içinde EMERGENT_LLM_KEY tanımlı olmalı.
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <div className="rounded-xl bg-[#141517] border border-white/10 px-5 py-3" data-testid="media-gallery-counter">
            <span className="text-3xl font-display text-[#D7B86A]">{gallery.length}</span>
            <div className="text-xs uppercase tracking-wide text-[#9A9387]">Galeri Fotoğrafı</div>
          </div>
          <div className="rounded-xl bg-[#141517] border border-white/10 px-5 py-3">
            <span className="inline-flex items-center gap-1.5 text-sm text-[#EDE7DA]">
              {logo ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-amber-400" />}
              Logo {logo ? "yüklendi" : "yok (metin logo kullanılıyor)"}
            </span>
            <div className="mt-1">
              <span className="inline-flex items-center gap-1.5 text-sm text-[#EDE7DA]">
                {hero ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-amber-400" />}
                Ana görsel {hero ? "yüklendi" : "yok (örnek görsel kullanılıyor)"}
              </span>
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={() => refetch()} className="border-white/15 text-[#EDE7DA] bg-transparent hover:bg-white/5">
          <RefreshCw className="w-4 h-4 mr-2" /> Yenile
        </Button>
      </div>

      {/* Logo + Hero */}
      <section className="grid md:grid-cols-2 gap-6">
        <div>
          <h2 className="font-display text-2xl text-[#F7F2E8] mb-3">Logo</h2>
          {logo && (
            <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#141517] p-4 mb-3" data-testid="logo-preview">
              <div className="h-16 w-40 grid place-items-center rounded-lg bg-[#0E0F10] border border-white/10 p-2">
                <img src={mediaUrl(logo.url)} alt="Logo" className="max-h-12 max-w-full object-contain" />
              </div>
              <div className="flex-1 text-xs text-[#9A9387] truncate">{logo.original_filename}</div>
              <Button size="sm" variant="outline" onClick={() => remove(logo.id, "Logo")} data-testid="logo-delete-button"
                className="h-8 border-white/15 text-[#EDE7DA] bg-transparent hover:bg-red-500/10 hover:text-red-300">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
          <DropZone kind="logo" multiple={false} onDone={refresh} testId="logo-dropzone"
            label={logo ? "Logoyu değiştir" : "Logo yükle"} hint="PNG (şeffaf) veya SVG önerilir · Header ve footer'da görünür" />
        </div>
        <div>
          <h2 className="font-display text-2xl text-[#F7F2E8] mb-3">Anasayfa Ana Görseli</h2>
          {hero && (
            <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#141517] p-4 mb-3" data-testid="hero-preview">
              <img src={mediaUrl(hero.thumb_url)} alt="Ana görsel" className="h-16 w-24 object-cover rounded-lg" />
              <div className="flex-1 text-xs text-[#9A9387] truncate">{hero.original_filename} · {hero.width}×{hero.height}</div>
              <Button size="sm" variant="outline" onClick={() => remove(hero.id, "Ana görsel")} data-testid="hero-delete-button"
                className="h-8 border-white/15 text-[#EDE7DA] bg-transparent hover:bg-red-500/10 hover:text-red-300">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
          <DropZone kind="hero" multiple={false} onDone={refresh} testId="hero-dropzone"
            label={hero ? "Ana görseli değiştir" : "Ana görsel yükle"} hint="Dikey/kare kompozisyon en iyi sonucu verir · Otomatik olarak 1920px'e küçültülür" />
        </div>
      </section>

      {/* Gallery */}
      <section>
        <div className="flex items-end justify-between gap-3 mb-3">
          <div>
            <h2 className="font-display text-2xl text-[#F7F2E8]">Galeri Fotoğrafları</h2>
            <p className="text-xs text-[#9A9387] mt-1">
              Yüklenen fotoğraflar anasayfa galerisi, /galeri sayfası ve ilçe sayfalarında görünür. Hiç fotoğraf yoksa örnek görseller kullanılır.
            </p>
          </div>
        </div>
        <DropZone kind="gallery" multiple onDone={refresh} testId="gallery-dropzone"
          label="Fotoğrafları buraya sürükleyin veya seçin" hint="JPG / PNG / WEBP · Tek seferde 30 dosyaya kadar · Otomatik boyutlandırma ve sıkıştırma" />

        {isLoading ? (
          <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8A24A]" /></div>
        ) : isError ? (
          <div className="py-16 text-center text-[#9A9387]">Görseller yüklenemedi. <button onClick={() => refetch()} className="text-[#D7B86A] underline">Tekrar dene</button></div>
        ) : gallery.length === 0 ? (
          <div className="py-14 text-center text-[#9A9387]" data-testid="gallery-empty">
            <ImageIcon className="w-10 h-10 mx-auto text-[#C8A24A]" />
            <p className="mt-3">Henüz fotoğraf yüklenmedi. Sitede örnek görseller gösteriliyor.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5" data-testid="gallery-grid">
            {gallery.map((g, i) => (
              <div key={g.id} className="rounded-2xl overflow-hidden border border-white/10 bg-[#141517]" data-testid="gallery-item">
                <div className="aspect-[4/3] bg-[#0E0F10]">
                  <img src={mediaUrl(g.thumb_url)} alt={g.alt || `Galeri ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-3 space-y-2">
                  <div className="flex items-center justify-between text-xs text-[#9A9387]">
                    <span>#{i + 1} · {g.width}×{g.height}</span>
                    <span>{Math.round((g.size || 0) / 1024)} KB</span>
                  </div>
                  <div className="flex gap-2">
                    <Input value={altDraft[g.id] ?? g.alt ?? ""} placeholder="Açıklama (alt metin)"
                      onChange={(e) => setAltDraft((d) => ({ ...d, [g.id]: e.target.value }))}
                      onBlur={() => saveAlt(g.id)}
                      className="h-8 text-xs bg-[#0E0F10] border-white/15 text-[#EDE7DA]" data-testid="gallery-alt-input" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1">
                      <Button size="icon" variant="outline" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Yukarı taşı"
                        className="h-8 w-8 border-white/15 text-[#EDE7DA] bg-transparent hover:bg-white/5" data-testid="gallery-move-up">
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="outline" disabled={i === gallery.length - 1} onClick={() => move(i, 1)} aria-label="Aşağı taşı"
                        className="h-8 w-8 border-white/15 text-[#EDE7DA] bg-transparent hover:bg-white/5" data-testid="gallery-move-down">
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => remove(g.id, "Fotoğraf")} data-testid="gallery-delete-button"
                      className="h-8 border-white/15 text-[#EDE7DA] bg-transparent hover:bg-red-500/10 hover:text-red-300">
                      <Trash2 className="w-4 h-4 mr-1" /> Sil
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
